import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/anime';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于奶片动漫',
  description: siteInfo.description,
  url: 'https://naipiananime.com/about',
  about: {
    '@type': 'Organization',
    name: siteInfo.name,
    description: siteInfo.description,
    foundingDate: '2020',
    email: siteInfo.email,
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://naipiananime.com' },
    { '@type': 'ListItem', position: 2, name: '关于我们' },
  ],
};

const brandImage = 'https://readdy.ai/api/search-image?query=Anime%20style%20illustration%20of%20a%20warm%20cozy%20anime%20studio%20workspace%20with%20drawing%20tablets%20and%20posters%20on%20the%20wall%2C%20warm%20coral%20and%20amber%20lighting%2C%20creative%20atmosphere%2C%20clean%20flat%20illustration%20style&width=1200&height=500&seq=about-brand&orientation=landscape';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const message = formData.get('message') as string;

    if (!name || !email || !message) {
      setFormError('请填写所有必填字段');
      setFormStatus('error');
      return;
    }

    if (message.length > 500) {
      setFormError('留言内容不能超过500个字符');
      setFormStatus('error');
      return;
    }

    try {
      const body = new URLSearchParams();
      body.append('name', name);
      body.append('email', email);
      body.append('message', message);

      const response = await fetch('https://readdy.ai/api/form/d9sn3rtlbrt1qcgaj690', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (!response.ok || !parsed || (parsed as Record<string, unknown>).code !== 'OK') {
        const serverMsg =
          (parsed as Record<string, unknown>)?.meta && typeof (parsed as Record<string, unknown>).meta === 'object'
            ? ((parsed as Record<string, unknown>).meta as Record<string, string>).message || ''
            : '';
        if (serverMsg.includes('spam')) {
          setFormStatus('success');
          return;
        }
        throw new Error(serverMsg || '提交失败，请稍后重试');
      }

      setFormStatus('success');
      form.reset();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : '提交失败，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description={`${siteInfo.name} - ${siteInfo.slogan}。${siteInfo.description}`}
        keywords="奶片动漫,关于我们,动漫平台介绍,品牌故事,联系方式"
        canonicalUrl="https://naipiananime.com/about"
        schema={{ ...aboutSchema, ...breadcrumbSchema } as unknown as Record<string, unknown>}
      />

      <main>
        <section className="relative w-full h-[260px] md:h-[360px] overflow-hidden">
          <img
            src={brandImage}
            alt="关于奶片动漫"
            title="关于奶片动漫 - 品牌故事"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white">关于我们</h1>
              <p className="text-sm md:text-base text-white/70 mt-2">{siteInfo.slogan}</p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
        </div>

        {/* Brand Introduction */}
        <section className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
            <a href="#brand-intro">品牌介绍</a>
          </h2>
          <div className="prose-custom space-y-5">
            <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
              {siteInfo.name}成立于{siteInfo.founded}，是一个专注于动漫内容的正版在线平台。我们致力于为每一位动漫爱好者提供优质的追番体验，让每一个热爱二次元的人都能在这里找到归属感。
            </p>
            <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
              平台涵盖热门新番、经典老番、国漫、日漫等海量资源，支持高清流畅播放和弹幕互动功能。无论你是喜欢热血战斗、温馨治愈还是悬疑推理，都能在奶片动漫找到心仪的作品。
            </p>
            <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
              奶片动漫始终坚持以用户体验为核心，不断优化播放技术和内容推荐算法，让每一位用户都能轻松发现适合自己的优秀动漫作品。我们与国内外多家版权方保持紧密合作，确保平台内容的丰富性和正版性。
            </p>
          </div>
        </section>

        {/* Reputation */}
        <section className="bg-background-100/70 py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              <a href="#reputation">平台口碑</a>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
              {siteInfo.reputation.map((item) => (
                <div key={item.label} className="bg-background-50 rounded-lg p-5 text-center border border-background-200/70">
                  <span className="w-10 h-10 mx-auto mb-3 flex items-center justify-center text-accent-500">
                    <i className={`${item.icon} text-2xl`}></i>
                  </span>
                  <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">{item.value}</p>
                  <p className="text-xs md:text-sm text-foreground-500">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="max-w-4xl mx-auto px-4 md:px-6 py-14">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
            <a href="#contact">联系我们</a>
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-900 mb-1">客服邮箱</p>
                  <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                  <i className="ri-weibo-line text-lg"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-900 mb-1">官方微博</p>
                  <p className="text-sm text-foreground-600">{siteInfo.weibo}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                  <i className="ri-wechat-line text-lg"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</p>
                  <p className="text-sm text-foreground-600">{siteInfo.wechat}</p>
                </div>
              </div>
            </div>

            <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6">
              <h3 className="text-base font-heading font-bold text-foreground-900 mb-4">在线留言</h3>
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div className="antibot-field">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入你的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入你的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                    placeholder="请输入你的留言内容（不超过500个字符）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>
                <button
                  type="submit"
                  className="w-full px-5 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  提交留言
                </button>
                {formStatus === 'success' && (
                  <p className="text-sm text-accent-600 text-center">感谢你的留言！我们将在24小时内回复你。</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-accent-600 text-center">{formError}</p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}